import {
  PageBlueprint,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import HomeIcon from '@material-ui/icons/Home';

/**
 * Custom replacement for the stock `page:catalog` extension shipped by
 * `@backstage/plugin-catalog/alpha`. We override it because the stock
 * loader (`NfsBaseCatalogPage`) renders a `<HeaderPage>` from
 * `@backstage/ui` showing "Bosch Digital Factory Catalog" + a Create
 * button, and that strip stacks UNDERNEATH our blue
 * `BoschPageLayout` banner — producing a duplicated header with the
 * Create button awkwardly centred (see screenshots in
 * docs/04-tema-visual.md → "Banners e PageLayout").
 *
 * The replacement page (`CatalogIndexPage` in
 * `packages/app/src/components/catalog/CatalogIndexPage.tsx`)
 * starts straight at `<Content>` and right-aligns the Create /
 * Support buttons in a `<ContentHeader>` row — same pattern used by
 * `/api-docs`, `/docs`, `/notifications`. The blue banner above
 * (rendered by `BoschPageLayout`) keeps the page title + subtitle.
 *
 * Both the upstream page AND the upstream nav item are disabled in
 * `app-config.yaml`:
 *
 *     extensions:
 *       - page:catalog: false
 *       - nav-item:catalog: false
 *
 * Without `page:catalog: false` two extensions would attach to
 * `/catalog` and react-router would render whichever resolved first.
 * Without `nav-item:catalog: false` we'd see two "Catalog" entries
 * in the sidebar (the upstream one pointing at the disabled page
 * and our new one).
 *
 * The sidebar config in `packages/app/src/modules/nav/Sidebar.tsx`
 * picks our nav item via `nav.take('page:catalog-override')` — the
 * extension id mirrors `pluginId: 'catalog-override'` below.
 *
 * Note we deliberately do NOT pass `noHeader: true`. We WANT our
 * `BoschPageLayout` banner ("Catalog" + subtitle from the
 * `PAGE_OVERRIDES` map in `pageLayout.tsx`) to render. The
 * `CatalogIndexPage` loader intentionally does NOT call `<Header>`
 * itself — it starts straight at `<Content>`.
 *
 * Note we do NOT replace `page:catalog/entity` (the per-entity page
 * at `/catalog/:namespace/:kind/:name`). That extension provides
 * the entity layout used by AboutCard, RelatedEntitiesCard, etc.
 * and is wired through dozens of `EntityCardBlueprint` and
 * `EntityContentBlueprint` extensions; we only swap the index page.
 *
 * ⚠️ ROUTE REF — REUSE THE UPSTREAM ONE.
 *
 * We pass `catalogPlugin.routes.catalogIndex` as the `routeRef` instead
 * of creating a fresh `createRouteRef()`. The upstream catalog plugin
 * declares `routes: { catalogIndex: rootRouteRef }` (see
 * node_modules/@backstage/plugin-catalog/dist/alpha/plugin.esm.js) and
 * dozens of internal components — `<EntityHeader>`, the unregister
 * dialog, search result items, breadcrumbs on entity pages — all call
 * `useRouteRef(catalogIndex)` to generate links back to the catalog
 * index. When we disable `page:catalog`, the upstream binding for
 * that routeRef goes away. If we then re-mount /catalog with a NEW
 * fresh routeRef, those internal `useRouteRef` calls throw at runtime:
 *
 *     No path for routeRef{id=catalog.catalogIndex,legacyId=catalog}
 *
 * which crashes the page when the user opens any
 * /catalog/:namespace/:kind/:name (Component, API, Doc — the entity
 * detail layout always renders <EntityHeader> at the top).
 *
 * Re-using the same routeRef wires our /catalog override into the
 * upstream's routing graph: `useRouteRef` resolves to /catalog (our
 * page), the breadcrumb back-link works, deep links into the catalog
 * from the search index work, etc.
 */
const catalogIndexPage = PageBlueprint.make({
  params: {
    path: '/catalog',
    title: 'Catalog',
    icon: <HomeIcon />,
    routeRef: catalogPlugin.routes.catalogIndex,
    loader: () =>
      import('../../components/catalog/CatalogIndexPage').then(m => (
        <m.CatalogIndexPage />
      )),
  },
});

/**
 * Registered as a frontend PLUGIN (not a module) because nav-item
 * auto-discovery from `PageBlueprint` only fires reliably when the
 * page lives inside a `createFrontendPlugin`. Same reason
 * `kindsModule`, `permissionsModule` and `techdocsModule` are
 * plugins. `pluginId: 'catalog-override'` avoids colliding with the
 * upstream `catalog` pluginId.
 *
 * No `routes` field — we don't expose any new route refs from this
 * override, we just re-bind the upstream `catalogPlugin.routes.catalogIndex`
 * via the PageBlueprint above.
 */
export const catalogModule = createFrontendPlugin({
  pluginId: 'catalog-override',
  extensions: [catalogIndexPage],
});
